import { useEffect, useRef, useState } from 'react';

/**
 * Detect when a sticky table header has pinned to the scrollport.
 * Uses a zero-height sentinel above the header — when it leaves the root,
 * the header is stuck and we can grow an opaque crown under the toolbar fade.
 *
 * `scopeSelector` must match an in-doc ancestor. The variables modal panel
 * has no match, so it stays unstuck (it already sticks in its own scroller).
 */
const useStickyTableHeader = ({
  scopeSelector = '.variables-block',
  fadePx = 12,
} = {}) => {
  const sentinelRef = useRef(null);
  const headerRef = useRef(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const header = headerRef.current;
    if (!sentinel || !header) return undefined;

    const inScope = header.closest(scopeSelector);
    if (!inScope) return undefined;

    const root =
      header.closest('.doc-sheet__body, .doc-fullpage__scroll') || null;

    const io = new IntersectionObserver(
      ([entry]) => {
        setStuck(!entry.isIntersecting);
      },
      { root, threshold: 0, rootMargin: `-${fadePx}px 0px 0px 0px` }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [fadePx, scopeSelector]);

  return { sentinelRef, headerRef, stuck };
};

export default useStickyTableHeader;
